import { styled } from '@mui/material/styles';
import MuiChip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextFieldBase from '@/components/mui/Form/TextField/TextFieldBase';

import {
  INPUT_MIN_SPACING,
  CHIP_GAP_SPACING,
} from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/constants';

// Lays out the OutlinedInput as a single flex row that hosts the chip strip inline before the
// growing text field. Uses the default outlined-input corner radius (per design), not a pill.
// Scoped to Workspace; never the shared base.
export const SearchField = styled(TextFieldBase)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius, // default squared corners (design)
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  // A flex item defaults to `min-width: auto` (its content's min size), so the chip strip won
  // out over the typing area — one long chip collapsed the field to a few characters.
  //
  // `marginRight` is restated from our own gap token rather than left at MUI's default: the overflow
  // budget has to account for this gap, and owning the value means the CSS and the arithmetic read
  // the same constant instead of the arithmetic guessing at a framework default.
  '& .MuiInputAdornment-positionStart': { minWidth: 0, marginRight: theme.spacing(CHIP_GAP_SPACING) },
  // Floor for the typing area, so the chip strip can never shrink it away. The overflow budget
  // subtracts the same token — but that only helps if the token MEANS the same thing on both sides.
  // MUI's InputBase sets the input to `content-box`, so `min-width` bounds the CONTENT box, while the
  // budget subtracts the token from an outer width. Autocomplete's own rule gives this input
  // `padding: 2.5px 4px 2.5px 8px` (4-class selector, so it also beats OutlinedInput's
  // `inputAdornedStart` padding-left reset), which made the two readings differ by exactly 12px:
  // measured budget 556 against a real lane allowance of 544, i.e. the lane over-filled by 12px and
  // the last chip was clipped. `border-box` makes the token an outer floor, which is what the
  // arithmetic already assumes — so the token cannot be misread rather than being compensated for.
  // Doubled selector so it wins over `.MuiAutocomplete-input` regardless of stylesheet order.
  '& .MuiOutlinedInput-input.MuiAutocomplete-input': {
    boxSizing: 'border-box',
    minWidth: theme.spacing(INPUT_MIN_SPACING),
  },
}));

// Shrinkable chip lane. `overflow: hidden` is a safety net only — with the measured threshold
// the strip never exceeds its budget. The "+N" counter is deliberately NOT inside this box: it
// is a sibling with the default `min-width: auto`, so a pathological clip eats the last chip and
// never the overflow indicator itself.
export const ChipStrip = styled(Stack)({ minWidth: 0, overflow: 'hidden' });

// Truncating filter chip: ellipsis when the label doesn't fit (full text via Tooltip).
export const FilterChip = styled(MuiChip)(({ theme }) => ({
  maxWidth: theme.spacing(25), // ~200px, token-driven; tunable (Q1)
  // `min-width: 0` is what makes the chip shrinkable AT ALL. `flex-shrink: 1` is MUI's default here
  // but is inert without this: a flex item's automatic minimum size is its min-content size, and a
  // Chip's min-content IS its full laid-out width. The `overflow: hidden` below sits on the LABEL,
  // and a descendant's overflow does not zero the ITEM's minimum — that rule only applies to a box
  // whose own overflow is not visible, and the Chip root computes `overflow: visible` (measured:
  // min-content 128.61px for a 128.61px chip). Without this line the lane shrank, the chips refused,
  // and the lane's `overflow: hidden` hard-clipped the last one.
  //
  // Why shrinkable at all: a ResizeObserver reports after layout and paint, so while the window is
  // being dragged one frame always carries the old chip count at the new width. Compressing degrades
  // that frame gracefully instead of cutting a chip. It costs the measurement nothing — widths come
  // from the hidden layer, which is `width: max-content` and so unaffected by shrinking.
  minWidth: 0,
  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
}));

// The overflow Popover's chip. Inside that surface the width bound belongs to the SURFACE, not to the
// chip: the paper is as wide as the input itself, so a long label may use the whole line instead of
// being cut at a per-chip constant that knows nothing about where it is rendered. The inline strip
// keeps FilterChip's own cap — there a chip really does compete with its neighbours for a fixed lane.
export const OverflowFilterChip = styled(FilterChip)({ maxWidth: '100%' });
